import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Users,
  MapPin,
  Phone,
  Mail,
  Star,
  Plus,
  X,
  Edit,
  Trash2,
  TrendingUp,
  DollarSign,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Archive,
  RotateCcw,
  Send,
  Camera,
  ShieldCheck,
} from "lucide-react";
import { addAgent, getAgents, updateAgent, deleteAgent, archiveAgent, restoreAgent, getArchivedAgents, resendAgentSetupEmail } from "../../api/agent";
import toast from "react-hot-toast";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import PhoneField from "../common/PhoneField";
import { toE164Value } from "../../utils/phoneNumber";
import { capitalizeEachWord } from "../../helper/function";
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from "../../lib/cache";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import UserAvatar from "../common/UserAvatar";
import { hasAdminAccess } from "../../utils/roleAccess";
import AccessPermissionsPage from "../../features/access/pages/AccessPermissionsPage";

const AgentManagement = () => {
  const currentUser = useSelector((state) => state.auth.user);
  const isAdmin = hasAdminAccess(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  // "agents" (the roster) or "access" (the permissions matrix). Kept in the URL
  // so the old /access-permissions link can redirect straight to it.
  const section = isAdmin && searchParams.get("tab") === "access" ? "access" : "agents";
  const setSection = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "access") params.set("tab", "access");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  const [agents, setAgents] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skeletonLoading, setSekeltonLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [viewAgent, setViewAgent] = useState(null);
  const [viewType, setViewType] = useState(localStorage.getItem("agentViewType") || "grid");
  const [activeTab, setActiveTab] = useState("active"); // "active" | "archived"

  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimeout = useRef(null);
  
  // Agent statistics states for accurate counts across all pages
  const [agentStats, setAgentStats] = useState({
    total: 0,
    active: 0,
    archived: 0
  });

  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    address: "",
    title: "",
    phone_number: "",
    zip_code: "",
    description: "",
    birth_date: "",
    profile_pic: null,
    profile_pic_url: "",
  });

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  // Fetch agent statistics separately
  const fetchAgentStats = async (search = "") => {
    try {
      const params = { page_size: 1000, ...(search && { search }) };
      const [activeRes, archivedRes] = await Promise.all([
        getAgents(params),
        getArchivedAgents({ page_size: 1, ...(search && { search }) }),
      ]);

      const activeData = activeRes.data;
      const allActive = activeData.results || (Array.isArray(activeData) ? activeData : []);
      const totalAgents = activeData.count || allActive.length;
      const activeCount = allActive.filter(a => a.is_active).length;

      const archivedData = archivedRes.data;
      const archivedCount = archivedData.count || 0;

      setAgentStats({ total: totalAgents, active: activeCount, archived: archivedCount });
    } catch (error) {
      console.error("Failed to fetch agent stats:", error);
    }
  };

  // Fetch agents from API with caching
  const fetchAgents = async (page = 1, search = "", forceRefresh = false, tab = null) => {
    const currentTab = tab ?? activeTab;
    try {
      const params = {
        page,
        page_size: pageSize,
        ...(search && { search })
      };

      // Only show skeleton loading on first load (when no cache exists)
      if (!apiCache.has(cacheKeys.agents(params)) || forceRefresh) {
        setSekeltonLoading(true);
      }

      // Use enhanced caching with stale-while-revalidate
      const fetcher = currentTab === 'archived'
        ? () => getArchivedAgents(params)
        : () => getAgents(params);

      const res = await cachedApiCall(
        cacheKeys.agents({ ...params, tab: currentTab }),
        fetcher,
        5 * 60 * 1000, // 5 minute cache
        { forceRefresh }
      );

      const data = res.data;

      // Handle paginated response
      if (data.results) {
        setAgents(data.results || []);
        setTotalCount(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize));
      } else {
        // Fallback for non-paginated response
        setAgents(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
        setTotalPages(1);
      }

      setSekeltonLoading(false);
    } catch (error) {
      setSekeltonLoading(false);
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to fetch agents.");
    }
  };

  // Reload when page changes
  useEffect(() => {
    fetchAgents(currentPage, searchQuery);
    fetchAgentStats();
  }, [currentPage, activeTab]);

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearchQuery(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Debounce search - wait 500ms after user stops typing
    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      fetchAgents(1, value);
    }, 500);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (agent) => {
    setEditAgent(agent);
    setForm({
      name: agent.name,
      email: agent.email,
      phone_number: toE164Value(agent.phone_number),
      address: agent.address,
      zip_code: agent.zip_code,
      title: agent.title,
      description: agent.description || "",
      birth_date: agent.birth_date || "",
      role: agent.role || "agent",
      department: agent.department || "sales",
      profile_pic: null,
      profile_pic_url: agent.profile_pic || "",
    });
    setIsEditModalOpen(true);
  };

  // Delete Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const handleResendSetupEmail = async (agent) => {
    setResendingId(agent.id);
    const toastId = toast.loading(`Resending setup email to ${agent.email}...`);
    try {
      await resendAgentSetupEmail(agent.id);
      toast.success(`Setup email resent to ${agent.email}`, { id: toastId });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to resend setup email", { id: toastId });
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = (agent) => {
    setAgentToDelete(agent);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!agentToDelete) return;

    setDeleteLoading(true);
    try {
      await archiveAgent(agentToDelete.id);

      // Immediately remove agent from UI state
      setAgents(prevAgents => prevAgents.filter(agent => agent.id !== agentToDelete.id));
      setTotalCount(prev => Math.max(0, prev - 1));

      toast.success("Agent archived successfully");

      // Invalidate cache and refresh to get accurate data
      invalidateCache.agents();

      setTimeout(() => {
        fetchAgents(currentPage, searchQuery, true);
        fetchAgentStats();
      }, 500);

      setIsDeleteModalOpen(false);
      setAgentToDelete(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to archive agent");
      console.error(error);
      fetchAgents(currentPage, searchQuery, true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (agent) => {
    try {
      await restoreAgent(agent.id);
      setAgents(prevAgents => prevAgents.filter(a => a.id !== agent.id));
      setTotalCount(prev => Math.max(0, prev - 1));
      toast.success(`${agent.name} has been restored`);
      invalidateCache.agents();
      setTimeout(() => {
        fetchAgents(currentPage, searchQuery, true);
        fetchAgentStats();
      }, 500);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to restore agent");
    }
  };


  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditAgent(null);
  };

  // --- UI ---
  // Agents and Access & Permissions are one tab now (Monday 13058000755,
  // Christian Gravias, 16 Sep 2026), so this page carries two sections and
  // remembers which one is open in the URL (?tab=access). Non-admins never
  // had the permissions page, so for them there is only the roster.
  // A segmented pill, not a second row of file tabs - the roster already has
  // its own Active/Archived tabs underneath and two identical tab strips read
  // as one confusing group.
  const sectionTabs = isAdmin ? (
    <div className="inline-flex bg-gray-100 p-1 rounded-xl">
      <button
        onClick={() => setSection("agents")}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
          section === "agents"
            ? "bg-white shadow-sm text-[#55C5D0]"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <Users className="w-4 h-4" />
        Agents
      </button>
      <button
        onClick={() => setSection("access")}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
          section === "access"
            ? "bg-white shadow-sm text-[#55C5D0]"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <ShieldCheck className="w-4 h-4" />
        Access &amp; Permissions
      </button>
    </div>
  ) : null;

  if (section === "access") {
    return (
      <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <div className="min-w-0">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            Agents Access &amp; Permissions
          </h2>
        </div>
        {sectionTabs}
        <AccessPermissionsPage embedded />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
      {/* Header - stacks on mobile so button doesn't overlap text */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {isAdmin ? "Agents Access & Permissions" : "Agents"}
          </h2>
          <p className="text-gray-600 text-sm sm:text-base mt-1 break-words">
            Manage your real estate agents and their territories
          </p>
        </div>
        {activeTab === "active" && isAdmin && (
          <button
            className="flex items-center justify-center space-x-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-white text-sm sm:text-base font-medium shadow-md transition-all duration-300 w-full sm:w-auto flex-shrink-0"
            style={{ backgroundColor: brandColors.secondary }}
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="w-4 h-4" />
            <span>Add Agent</span>
          </button>
        )}
      </div>

      {sectionTabs}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => { setActiveTab("active"); setCurrentPage(1); setAgents([]); }}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === "active"
              ? "bg-white border border-b-white border-gray-200 text-[#55C5D0] -mb-px"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Active Agents
        </button>
        <button
          onClick={() => { setActiveTab("archived"); setCurrentPage(1); setAgents([]); }}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === "archived"
              ? "bg-white border border-b-white border-gray-200 text-[#55C5D0] -mb-px"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Archive className="w-4 h-4" />
          Archived
          {agentStats.archived > 0 && (
            <span className="bg-gray-200 text-gray-600 text-xs rounded-full px-1.5 py-0.5">
              {agentStats.archived}
            </span>
          )}
        </button>
      </div>

      {/* Search Bar & View Toggle */}
      <div className="bg-white rounded-2xl shadow border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search agents by name, email, phone, or address..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
            />
          </div>
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => {
                setViewType("grid");
                localStorage.setItem("agentViewType", "grid");
              }}
              className={`p-2 rounded-lg transition-all ${viewType === "grid"
                ? "bg-white shadow-sm text-[#55C5D0]"
                : "text-gray-500 hover:text-gray-700"
                }`}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setViewType("list");
                localStorage.setItem("agentViewType", "list");
              }}
              className={`p-2 rounded-lg transition-all ${viewType === "list"
                ? "bg-white shadow-sm text-[#55C5D0]"
                : "text-gray-500 hover:text-gray-700"
                }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-6 flex flex-col items-start">
          <div className="flex items-center justify-between w-full mb-2">
            <Users className="w-6 h-6" style={{ color: brandColors.primary }} />
            <span className="text-gray-500 text-sm font-medium">
              Total Agents
            </span>
          </div>
          <span className="text-2xl font-bold text-gray-900">
            {agentStats.total}
          </span>
        </div>
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-6 flex flex-col items-start">
          <div className="flex items-center justify-between w-full mb-2">
            <Users className="w-6 h-6" style={{ color: "#22c55e" }} />
            <span className="text-gray-500 text-sm font-medium">
              Active Agents
            </span>
          </div>
          <span className="text-2xl font-bold text-gray-900">
            {agentStats.active}
          </span>
        </div>
        <div
          className="bg-white rounded-2xl shadow border border-gray-100 p-6 flex flex-col items-start cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => { setActiveTab("archived"); setCurrentPage(1); setAgents([]); }}
        >
          <div className="flex items-center justify-between w-full mb-2">
            <Archive className="w-6 h-6" style={{ color: "#ef4444" }} />
            <span className="text-gray-500 text-sm font-medium">
              Archived Agents
            </span>
          </div>
          <span className="text-2xl font-bold text-gray-900">
            {agentStats.archived}
          </span>
        </div>
      </div>

      {/* Agents View */}
      {skeletonLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-pulse"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
                <div className="w-16 h-6 bg-gray-100 rounded-full" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-5/6" />
                <div className="h-3 bg-gray-100 rounded w-4/6" />
                <div className="h-3 bg-gray-100 rounded w-3/6" />
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                <div className="h-3 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <div className="w-8 h-8 bg-gray-200 rounded-full" />
                <div className="w-8 h-8 bg-gray-200 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : agents.length > 0 ? (
        viewType === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 relative flex flex-col"
                style={{ borderTop: `4px solid ${brandColors.secondary}` }}
              >
                <div className="flex items-center space-x-4 mb-2">
                  <UserAvatar
                    src={agent.profile_pic}
                    name={agent.name}
                    size={48}
                    bgColor={brandColors.primary}
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-0.5">
                      {capitalizeEachWord(agent?.name || "No Name")}
                    </h3>
                    <p className="text-sm text-gray-500 mb-0.5">
                      {agent.title || agent.territory || "No Title"}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${agent.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {agent.is_active ? "active" : "inactive"}
                      </span>
                      {agent.role === "dev" && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700">
                          dev
                        </span>
                      )}
                      {agent.role === "super_admin" && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                          super admin
                        </span>
                      )}
                      {!agent.is_email_verified && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                          setup pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    {agent.email}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    {agent.phone_number || "N/A"}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    {agent.address || "No address"}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-auto">
                  {activeTab === "archived" && isAdmin && (
                    <button
                      onClick={() => handleRestore(agent)}
                      className="p-2 text-sm font-medium rounded-xl bg-green-100 text-green-700 hover:bg-green-200 transition-all duration-200 flex items-center gap-1"
                      title="Restore Agent"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  {activeTab === "active" && isAdmin && (
                    <>
                      {!agent.is_email_verified && (
                        <button
                          onClick={() => handleResendSetupEmail(agent)}
                          disabled={resendingId === agent.id}
                          className="p-2 text-sm font-medium rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all duration-200 disabled:opacity-50"
                          title="Resend Setup Email"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(agent)}
                        className="p-2 text-sm font-medium rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(agent)}
                        className="p-2 text-sm font-medium rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all duration-200"
                        title="Archive Agent"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setViewAgent(agent)}
                    className="p-2 text-sm font-medium rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] hover:bg-[#55C5D0]/20 transition-all duration-200"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900">Agent</th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Title</th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Phone</th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Status</th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {agents.map((agent) => (
                    <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <UserAvatar
                            src={agent.profile_pic}
                            name={agent.name}
                            size={40}
                            bgColor={brandColors.primary}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {capitalizeEachWord(agent?.name || "No Name")}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{agent.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {agent.title || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {agent.phone_number || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${agent.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                            }`}
                        >
                          {agent.is_active ? "active" : "inactive"}
                        </span>
                        {agent.role === "dev" && (
                          <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            dev
                          </span>
                        )}
                        {agent.role === "super_admin" && (
                          <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            super admin
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {activeTab === "archived" && isAdmin && (
                            <button
                              onClick={() => handleRestore(agent)}
                              className="p-2 text-green-600 hover:text-green-800 transition-colors"
                              title="Restore Agent"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {activeTab === "active" && isAdmin && (
                            <>
                              {!agent.is_email_verified && (
                                <button
                                  onClick={() => handleResendSetupEmail(agent)}
                                  disabled={resendingId === agent.id}
                                  className="p-2 text-amber-600 hover:text-amber-800 transition-colors disabled:opacity-50"
                                  title="Resend Setup Email"
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(agent)}
                                className="p-2 text-gray-500 hover:text-[#55C5D0] transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(agent)}
                                className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                                title="Archive Agent"
                              >
                                <Archive className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setViewAgent(agent)}
                            className="p-2 text-gray-500 hover:text-[#55C5D0] transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-12">
          {activeTab === "archived" ? (
            <Archive className="mx-auto h-12 w-12 text-gray-400" />
          ) : (
            <Users className="mx-auto h-12 w-12 text-gray-400" />
          )}
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {activeTab === "archived" ? "No Archived Agents" : "No Agents Found"}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeTab === "archived"
              ? "Archived agents will appear here."
              : "Get started by adding a new agent."}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {agents.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount} agents
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageClick(pageNum)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                        ? "bg-[#55C5D0] text-white"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Agent Modal */}
      {isAddModalOpen && (
        <AgentModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={async (data) => {
            setLoading(true);
            try {
              await addAgent(data);
              toast.success("Agent added successfully!");
              setIsAddModalOpen(false);

              // Invalidate cache
              invalidateCache.agents();

              // Refresh after a short delay to ensure backend is updated
              setTimeout(() => {
                fetchAgents(currentPage, searchQuery, true);
                fetchAgentStats(); // Refresh stats after add
              }, 500);
            } catch (error) {
              toast.error(
                error?.response?.data?.detail || "Failed to add agent"
              );
            } finally {
              setLoading(false);
            }
          }}
          loading={loading}
        />
      )}
      {/* Edit Agent Modal */}
      {isEditModalOpen && (
        <AgentModal
          isOpen={isEditModalOpen}
          initialValues={form}
          onClose={handleCloseModal}
          onSubmit={async (data) => {
            setLoading(true);

            try {
              await updateAgent(editAgent.id, data);
              toast.success("Agent updated successfully!");
              setIsEditModalOpen(false);
              setEditAgent(null);

              // Invalidate cache
              invalidateCache.agents();

              // Refresh after a short delay to ensure backend is updated
              setTimeout(() => {
                fetchAgents(currentPage, searchQuery, true);
                fetchAgentStats(); // Refresh stats after update
              }, 500);
            } catch (error) {
              toast.error(
                error?.response?.data?.detail || "Failed to update agent"
              );
            } finally {
              setLoading(false);
            }
          }}
          loading={loading}
          isEdit
          isSelf={editAgent?.id === currentUser?.id}
        />
      )}

      {/* Archive Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Archive Agent"
        description="This will remove the agent from the Agents tab and disable their login. You can restore them from the Archived tab at any time."
        itemName={agentToDelete?.name}
        loading={deleteLoading}
      />

      {/* View Agent Modal */}
      {viewAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setViewAgent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              title="Close"
            >
              <X size={24} />
            </button>
            <div className="p-8">
              <div className="flex items-center space-x-4 mb-4">
                <UserAvatar
                  src={viewAgent.profile_pic}
                  name={viewAgent.name}
                  size={56}
                  bgColor={brandColors.primary}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-0.5">
                    {capitalizeEachWord(viewAgent?.name || "")}
                  </h3>
                  <p className="text-sm text-gray-500 mb-0.5">
                    {viewAgent.title || viewAgent.territory}
                  </p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${viewAgent.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                      }`}
                  >
                    {viewAgent.is_active ? "active" : "inactive"}
                  </span>
                </div>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  {viewAgent.email}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="w-4 h-4 mr-2" />
                  {viewAgent.phone_number || "N/A"}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  {viewAgent.address || "No address"}
                </div>
                {viewAgent.description && (
                  <div className="text-xs text-start text-gray-500 mt-2 whitespace-pre-line">
                    {viewAgent.description}
                  </div>
                )}
              </div>
              {/* <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 mb-2">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Properties Sold</p>
                  <p className="text-lg font-bold text-gray-900">
                    {viewAgent.performance?.leadsAssigned || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Revenue</p>
                  <p className="text-lg font-bold text-green-600">
                    ${(viewAgent.performance?.revenue || 0).toLocaleString()}
                  </p>
                </div>
                <div className="col-span-2 flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 text-xs font-semibold">
                    <Star className="w-4 h-4 mr-1 text-yellow-400 fill-current" />
                    {viewAgent.performance?.rating || "--"} Rating
                  </span>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AgentModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues = {},
  isEdit,
  isSelf = false,
  loading = false,
}) => {
  const [form, setForm] = useState({
    email: initialValues.email || "",
    password: "",
    name: initialValues.name || "",
    address: initialValues.address || "",
    title: initialValues.title || "",
    phone_number: initialValues.phone_number || "",
    zip_code: initialValues.zip_code || "",
    description: initialValues.description || "",
    birth_date: initialValues.birth_date || "",
    role: initialValues.role || "agent",
    department: initialValues.department || "sales",
  });
  const [phoneError, setPhoneError] = useState("");
  // Profile picture state — `picFile` is the new File to upload (if any),
  // `picPreview` is what we render (an object URL for new files, otherwise the existing URL).
  // `picRemoved` tracks the explicit intent to clear the existing photo so we
  // can send a `clear_profile_pic` flag to the backend on submit.
  const [picFile, setPicFile] = useState(null);
  const [picPreview, setPicPreview] = useState(initialValues.profile_pic_url || "");
  const [picRemoved, setPicRemoved] = useState(false);
  const [picError, setPicError] = useState("");
  const fileInputRef = useRef(null);

  // Revoke object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (picPreview && picPreview.startsWith("blob:")) {
        URL.revokeObjectURL(picPreview);
      }
    };
  }, [picPreview]);

  const handlePicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPicError("Please select an image file (JPG, PNG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPicError("Image must be smaller than 5 MB");
      return;
    }
    setPicError("");
    setPicFile(file);
    setPicPreview(URL.createObjectURL(file));
    // Picking a new file cancels any pending "remove" action
    setPicRemoved(false);
  };

  const handlePicRemove = () => {
    setPicFile(null);
    setPicPreview("");
    setPicError("");
    // Mark the existing photo for removal so the backend clears it on save
    setPicRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.phone_number || !isValidPhoneNumber(form.phone_number)) {
      setPhoneError("Invalid phone number");
      return;
    }

    // Format the data to ensure birth_date is in YYYY-MM-DD format. Strip
    // empty optional fields so PATCH semantics work correctly on the backend.
    const formattedData = {
      ...form,
      birth_date: form.birth_date ? form.birth_date : null,
    };
    if (!formattedData.password) delete formattedData.password;
    // Role can only be changed when editing an existing agent.
    if (!isEdit) delete formattedData.role;
    // The role dropdown is disabled when you are editing your own account, so
    // don't send it back at all - the backend rejects a self role change.
    if (isSelf) delete formattedData.role;
    if (picFile) {
      formattedData.profile_pic = picFile;
    } else if (picRemoved) {
      // Explicit signal to the backend to clear the stored profile picture
      formattedData.clear_profile_pic = "true";
    }

    onSubmit(formattedData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-[modalIn_0.25s_ease]">
        {/* Modal Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-10 py-7 border-b bg-[#f7fafc] rounded-t-2xl shadow-sm">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
            {isEdit ? "Edit Agent" : "Add New Agent"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-10 py-8 space-y-10">
          {/* Profile Picture Section */}
          <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-6">
              <Camera className="w-5 h-5 text-[#55C5D0] mr-2" />
              <h3 className="text-xl font-semibold text-gray-800">Profile Picture</h3>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-shrink-0">
                {picPreview ? (
                  <img
                    src={picPreview}
                    alt={form.name || "Agent profile"}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[#55C5D0]/10 border-4 border-white shadow-md flex items-center justify-center text-2xl font-semibold text-[#55C5D0]">
                    {(form.name || "A")
                      .split(" ")
                      .filter(Boolean)
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePicChange}
                  className="hidden"
                />
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] transition-colors"
                  >
                    {picPreview ? "Change Photo" : "Upload Photo"}
                  </button>
                  {picPreview && (
                    <button
                      type="button"
                      onClick={handlePicRemove}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  JPG or PNG, up to 5 MB. Square images look best.
                </p>
                {picError && (
                  <p className="text-xs text-red-500 mt-1">{picError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-6">
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                className="w-5 h-5 text-[#55C5D0] mr-2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-800">
                Personal Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter agent's full name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  disabled={isEdit}
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent disabled:bg-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneField
                  name="phone_number"
                  value={form.phone_number}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, phone_number: value }));
                    if (!value) {
                      setPhoneError("Phone number is required");
                    } else if (!isValidPhoneNumber(value)) {
                      setPhoneError("Invalid phone number");
                    } else {
                      setPhoneError("");
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                  placeholder="Enter phone number"
                  required
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password{" "}
                  {isEdit && (
                    <span className="text-xs text-gray-400">
                      (leave blank to keep unchanged)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={isEdit ? "••••••••" : "Enter password"}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="birth_date"
                  value={form.birth_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Role
                  </label>
                  <select
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    disabled={isSelf}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="dev">Dev</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {isSelf
                      ? "You cannot change your own role."
                      : form.role === "dev"
                      ? "Dev is for the media / tech team. They use the CRM like an agent but are left off the sales leaderboard."
                      : form.role === "super_admin"
                      ? "Super Admin is the agency principal. Everything an Admin can do, plus Agent Analytics, Executive, the agency-wide views by default, and the office-wide alert emails."
                      : "Admins manage agents, properties and CRM settings, and see Portal Analytics. Their own pipeline, contracts and emails are their own; Agent Analytics and Executive are Super Admin only."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Professional Information Section */}
          <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-6">
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                className="w-5 h-5 text-[#55C5D0] mr-2"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-800">
                Professional Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Enter job title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zip Code
                </label>
                <input
                  type="text"
                  name="zip_code"
                  value={form.zip_code}
                  onChange={handleChange}
                  placeholder="Enter zip code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <select
                  name="department"
                  value={form.department}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                >
                  <option value="sales">Sales</option>
                  <option value="rental">Rental</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Which side of the Agents analytics report this agent shows up on.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Enter full address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter agent description..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="px-8 py-3 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              disabled={loading || (isEdit && !form.name)}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                  {isEdit ? "Updating..." : "Adding..."}
                </span>
              ) : isEdit ? (
                "Update Agent"
              ) : (
                "Add Agent"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentManagement;
